import React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material/styles';
import { ClickAwayListener, Grow, IconButton, MenuItem, MenuList, Paper, Popper, Stack } from '@mui/material';
import { ItemRowMenuItemType } from '../../../types/ui/ItemRowMenuItem';
import CustomTooltip from '../tooltip/CustomTooltip';

type ItemRowMenuItemsType = {
  dropdownActions?: ItemRowMenuItemType[];
};
export default function ItemRowMenuItems({ dropdownActions }: ItemRowMenuItemsType) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const menuListRef = React.useRef<HTMLUListElement>(null);
  const theme = useTheme();

  if (!dropdownActions || dropdownActions.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const focusFirstMenuItem = () => {
    const firstMenuItem = menuListRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])',
    );
    firstMenuItem?.focus();
  };

  const handleAnchorKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open || event.key !== 'Tab' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    focusFirstMenuItem();
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as Node;
    if (document.querySelector('.MuiDialog-root')?.contains(target)) {
      return;
    }
    handleClose();
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      // event.preventDefault();
      // setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div>
      <CustomTooltip className="action-button-tooltip" title="More" aria-label="More">
        <IconButton
          ref={anchorRef}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          onKeyDown={handleAnchorKeyDown}
        >
          <MoreVertIcon />
        </IconButton>
      </CustomTooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        sx={{ zIndex: 1 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList
                  ref={menuListRef}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                  role="menu"
                >
                  {dropdownActions.map((action) => (
                    <MenuItem
                      key={`menu-item-${action.id}`}
                      data-test={`menu-item-${action.id}`}
                      onClick={() => {
                        action.onClick(() => handleClose());
                      }}
                      role="menuitem"
                    >
                      <Stack
                        direction="row"
                        gap={2}
                        alignItems="center"
                        sx={{
                          color: theme.palette.textSecondary.main,
                        }}
                      >
                        {action.icon} {action.label}
                      </Stack>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {dropdownActions.map((action) =>
        action.childrenComponentFunction ? (
          <React.Fragment key={`menu-dialog-${action.id}`}>
            {action.childrenComponentFunction(() => handleClose())}
          </React.Fragment>
        ) : null,
      )}
    </div>
  );
}
